import { useAuthUser } from "../../contexts/AuthUserContext";
import { useCustomer } from "../../contexts/CustomerContext";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { clientsData } from "../../api/clientData";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useState, useEffect } from "react";
import heliosLogoLight from '../../assets/common/HCAI Light.png';
import heliosLogoDark from '../../assets/common/HCAI Dark.png';
import { useNavigation } from "../../hooks/useNavigation";
import { useTheme } from "../../contexts/ThemeContext";
import WeekPicker from "../calendar/WeekPicker";
import CustomerImpersonation from "../admin/CustomerImpersonation";

import LogoutButton from "../auth/LogoutButton";
import ProfileButton from "./ProfileButton";

// Custom NavLink wrapper that detects same-page clicks and triggers reset
const ResetNavLink = ({ to, children, className, onClick, ...props }) => {
    const location = useLocation();
    const navigate = useNavigate();
    
    const handleClick = (e) => {
        // Always dispatch a reset event - let each page decide if it needs to reset
        // This approach works regardless of URL changes or internal state
        window.dispatchEvent(new CustomEvent('pageReset', { 
            detail: { 
                path: to,
                currentPath: location.pathname,
                timestamp: Date.now()
            } 
        }));
        
        // Call the original onClick if provided
        if (onClick) {
            onClick(e);
        }
    };
    
    return (
        <NavLink to={to} className={className} onClick={handleClick} {...props}>
            {children}
        </NavLink>
    );
};

// Enhanced Responsive Mobile Navbar
const ResponsiveMobileNav = ({ user, isAdmin, searchQuery, setSearchQuery, handleSearch, searchInputRef, isRefreshing, handleGlobalRefresh, showMobileMenu, setShowMobileMenu }) => {
    const navigate = useNavigate();
    const [activeView, setActiveView] = useState(isAdmin ? 'admin' : 'client');
    const [isTransitioning, setIsTransitioning] = useState(false);
    const { currentCustomer, isImpersonating } = useCustomer();
    const { theme } = useTheme();

    const { navLinks: adminNavLinks } = useNavigation('admin');
    const { navLinks: clientNavLinks } = useNavigation('client');

    const heliosLogo = theme === 'dark' ? heliosLogoDark : heliosLogoLight;

    // Categorize admin nav links
    const wardsNavItems = adminNavLinks.filter(nav => nav.path === 'andy-notes' || nav.path === 'equipment-review' || nav.path === 'policy-review');
    const administratorNavItems = adminNavLinks.filter(nav => nav.path !== 'andy-notes' && nav.path !== 'equipment-review' && nav.path !== 'policy-review');

    const [isWardsExpanded, setIsWardsExpanded] = useState(true);
    const [isAdministratorExpanded, setIsAdministratorExpanded] = useState(true);
    const [isWardsTransitioning, setIsWardsTransitioning] = useState(false);
    const [isAdministratorTransitioning, setIsAdministratorTransitioning] = useState(false);

    const toggleAdministrator = () => {
        if (isAdministratorTransitioning) return;
        setIsAdministratorTransitioning(true);
        setTimeout(() => {
            setIsAdministratorExpanded(!isAdministratorExpanded);
            setTimeout(() => {
                setIsAdministratorTransitioning(false);
            }, 50);
        }, 150);
    };

    const toggleWards = () => {
        if (isWardsTransitioning) return;
        setIsWardsTransitioning(true);
        setTimeout(() => {
            setIsWardsExpanded(!isWardsExpanded);
            setTimeout(() => {
                setIsWardsTransitioning(false);
            }, 50);
        }, 150);
    };

    // Get client logo based on current customer
    const getClientLogo = () => {
        if (currentCustomer) {
            // Check multiple possible logo sources for compatibility
            if (currentCustomer.branding?.logo_url) {
                return currentCustomer.branding.logo_url;
            }
            // Fallback to logoUrl property (for admin impersonation)
            if (currentCustomer.logoUrl) {
                return currentCustomer.logoUrl;
            }
            // Fallback to branding property without logo_url (if structure is different)
            if (currentCustomer.branding && typeof currentCustomer.branding === 'string') {
                return currentCustomer.branding;
            }
            
            // Fallback to clientsData for regular client users
            const customerData = clientsData.find(client =>
                client.id === currentCustomer.id ||
                client.clientId === currentCustomer.clientId ||
                client.name.toLowerCase() === currentCustomer.name.toLowerCase()
            );
            if (customerData?.logo) {
                return customerData.logo;
            }
        }
        // Fall back to Helios logo if no client logo is found
        return heliosLogo;
    };

    // Get the appropriate logo based on current customer context (same as AdminNav)
    const getCurrentLogo = () => {
        if (activeView === 'client' && currentCustomer) {
            // First check if customer has a blob storage logo URL
            if (currentCustomer.logoUrl) {
                return currentCustomer.logoUrl;
            }

            // Fall back to static clientsData
            const customerData = clientsData.find(client =>
                client.id === currentCustomer.id ||
                client.clientId === currentCustomer.clientId ||
                client.name.toLowerCase() === currentCustomer.name.toLowerCase()
            );
            return customerData?.logo || heliosLogo;
        }
        // Default to Helios logo for admin
        return heliosLogo;
    };

    const getCurrentCustomerName = () => {
        if (activeView === 'client' && currentCustomer) {
            return currentCustomer.name;
        }
        return "Helios Core";
    };

    const toggleView = () => {
        if (isTransitioning) return; // Prevent multiple rapid toggles
        
        setIsTransitioning(true);
        // Start fade out
        setTimeout(() => {
            setActiveView(prev => prev === 'admin' ? 'client' : 'admin');
            // Start fade in after state change
            setTimeout(() => {
                setIsTransitioning(false);
            }, 50); // Small delay to ensure state change is processed
        }, 150); // Half of transition duration
    };

    // Auto-switch to client view when customer selection happens
    useEffect(() => {
        if (isImpersonating && activeView === 'admin') {
            setActiveView('client');
        }
    }, [isImpersonating]); // Only trigger when impersonating state changes, not when activeView changes

    // Auto-switch to admin view when impersonation ends
    useEffect(() => {
        if (!isImpersonating && activeView === 'client') {
            setActiveView('admin');
        }
    }, [isImpersonating]); // Only trigger when impersonating state changes, not when activeView changes

    const currentNavItems = isAdmin ? (activeView === 'admin' ? adminNavLinks : clientNavLinks) : clientNavLinks;

    return (
        <>
            {/* Black Overlay */}
            {showMobileMenu && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-30 z-30"
                    onClick={() => setShowMobileMenu(false)}
                ></div>
            )}

            {/* Mobile Sidebar - slides in from right */}
            <div className={`fixed top-0 right-0 h-full w-80 bg-background z-40 transform transition-transform duration-300 ease-in-out ${
                showMobileMenu ? 'translate-x-0' : 'translate-x-full'
            }`}>
                <div className="flex flex-col h-full">
                    {/* Header with Logo */}
                    {/* <div className="px-2 py-3 border-b border-bgAccent">
                        <img 
                            src={getCurrentLogo()} 
                            className={`w-full h-12 object-contain navbar-logo-transition ${isTransitioning ? 'navbar-logo-fade-out' : 'navbar-logo-fade-in'}`} 
                            alt={`${getCurrentCustomerName()} Logo`} 
                        />
                    </div> */}

                    {/* Toggle Buttons for helios_admin */}
                    {isAdmin && (
                        <>
                            <div className="px-2 py-3 border-b border-bgAccent">
                                <CustomerImpersonation />
                            </div>
                            <div className="px-2 py-3 border-b border-bgAccent">
                                <div
                                    className={`flex flex-row justify-between items-center p-2 rounded-t-md cursor-pointer ${activeView === 'admin' ? 'bg-primary-20 border-primary-50' : 'bg-accent-20 border-accent-50'}`}
                                    onClick={toggleView}
                                >
                                    <span className="flex flex-row gap-1 items-center font-semibold">
                                        <Icon icon={activeView === 'admin' ? "mdi:administrator" : "mdi:user"} className={`text-xl ${activeView === 'admin' ? 'text-primary' : 'text-accent'}`} />
                                        {activeView === 'admin' ? "Admin" : "Client"}
                                    </span>
                                    <Icon icon={activeView === 'admin' ? 'mingcute:toggle-left-fill' : 'mingcute:toggle-right-fill'} className={`responsive-navbar-toggle ${activeView === 'admin' ? 'text-primary' : 'text-accent'}`} />
                                </div>
                            </div>
                        </>
                    )}

                    {/* WeekPicker */}
                    <div className="p-2 border-b border-bgAccent flex flex-row justify-between items-center gap-4">
                        <WeekPicker />
                        <button
                            onClick={() => {
                                handleGlobalRefresh();
                                setShowMobileMenu(false);
                            }}
                            disabled={isRefreshing}
                            className="flex-1 p-2 button-container rounded-md flex flex-row gap-2 items-center justify-center text-font-primary hover:shadow-neo-card transition-all duration-200 disabled:opacity-50"
                        >
                            <Icon icon="ri:refresh-line" className={`text-2xl text-accent ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

                    {/* Search Bar */}
                    <div className="px-2 py-3 border-b border-bgAccent">
                        <form
                            role="search"
                            className="search-bar input-textbox rounded-md flex-grow min-w-0"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSearch(searchQuery);
                                setShowMobileMenu(false);
                            }}
                        >
                            <Icon icon="mingcute:search-line" className="text-xl text-accent" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="search-input w-full focus:outline-none"
                                placeholder="Search... (Ctrl+K)"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch(e.target.value);
                                        setShowMobileMenu(false);
                                    }
                                }}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="text-accent hover:text-primary"
                                >
                                    <Icon icon="material-symbols:close" className="text-lg" />
                                </button>
                            )}
                        </form>
                    </div>

                    {/* Refresh Button */}
                    

                    {/* Navigation Items */}
                    <div className="flex-1 p-2 scrollable overflow-y-auto">
                        <nav className={`flex flex-col gap-1 navbar-nav-transition ${isTransitioning ? 'navbar-nav-fade-out' : 'navbar-nav-fade-in'}`}>
                            {isAdmin && activeView === 'admin' ? (
                                <>
                                    {/* Administrator Dropdown Section */}
                                    {administratorNavItems.length > 0 && (
                                        <div className="flex flex-col gap-1">
                                            <button
                                                onClick={toggleAdministrator}
                                                className={`nav-item-mobile py-1.5 px-1 hover:rounded-none border-b ${isAdministratorExpanded ? 'border-primary' : 'border-font-secondary hover:border-primary'} hover:bg-transparent font-medium w-full transition-colors duration-300 ease-out-in group flex flex-row items-center justify-between`}
                                            >
                                                <span className={`text-xs font-extrabold uppercase truncate overflow-hidden ${isAdministratorExpanded ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`}>Administrator</span>
                                                <Icon 
                                                    icon={isAdministratorExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} 
                                                    className={`text-xl ${isAdministratorExpanded ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`}
                                                />
                                            </button>
                                            <div className={`flex flex-col gap-1 navbar-nav-transition ${!isAdministratorExpanded && !isAdministratorTransitioning ? 'hidden' : (isAdministratorTransitioning && !isAdministratorExpanded ? 'navbar-nav-fade-out' : 'navbar-nav-fade-in')}`}>
                                                {administratorNavItems.map((navItem) => (
                                                    <ResetNavLink
                                                        key={navItem.path}
                                                        to={navItem.path}
                                                        onClick={() => {
                                                            setShowMobileMenu(false);
                                                            toggleAdministrator();
                                                        }}
                                                        className={({ isActive }) => isActive 
                                                            ? "nav-item-mobile-active font-bold w-full transition-colors duration-300 ease-out-in" 
                                                            : "nav-item-mobile font-medium w-full transition-colors duration-300 ease-out-in group"
                                                        }
                                                    >
                                                        {({ isActive }) => (
                                                            <>
                                                                <Icon icon={navItem.icon} className={`text-xl flex-shrink-0 group-hover:text-primary ${isActive ? 'text-primary' : ''}`} />
                                                                <span className="truncate overflow-hidden group-hover:text-font-primary group-hover:font-semibold">{navItem.title}</span>
                                                            </>
                                                        )}
                                                    </ResetNavLink>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Wards Dropdown Section */}
                                    {wardsNavItems.length > 0 && (
                                        <div className="flex flex-col gap-1">
                                            <button
                                                onClick={toggleWards}
                                                className={`nav-item-mobile py-1.5 px-1 hover:rounded-none border-b ${isWardsExpanded ? 'border-primary' : 'border-font-secondary hover:border-primary'} hover:bg-transparent font-medium w-full transition-colors duration-300 ease-out-in group flex flex-row items-center justify-between`}
                                            >
                                                <span className={`text-xs font-extrabold uppercase truncate overflow-hidden ${isWardsExpanded ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`}>Wards</span>
                                                <Icon 
                                                    icon={isWardsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} 
                                                    className={`text-xl ${isWardsExpanded ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`}
                                                />
                                            </button>
                                            <div className={`flex flex-col gap-1 navbar-nav-transition ${!isWardsExpanded && !isWardsTransitioning ? 'hidden' : (isWardsTransitioning && !isWardsExpanded ? 'navbar-nav-fade-out' : 'navbar-nav-fade-in')}`}>
                                                {wardsNavItems.map((navItem) => (
                                                    <ResetNavLink
                                                        key={navItem.path}
                                                        to={navItem.path}
                                                        onClick={() => {
                                                            setShowMobileMenu(false);
                                                            toggleWards();
                                                        }}
                                                        className={({ isActive }) => isActive 
                                                            ? "nav-item-mobile-active font-bold w-full transition-colors duration-300 ease-out-in" 
                                                            : "nav-item-mobile font-medium w-full transition-colors duration-300 ease-out-in group"
                                                        }
                                                    >
                                                        {({ isActive }) => (
                                                            <>
                                                                <Icon icon={navItem.icon} className={`text-xl flex-shrink-0 group-hover:text-primary ${isActive ? 'text-primary' : ''}`} />
                                                                <span className="truncate overflow-hidden group-hover:text-font-primary group-hover:font-semibold">{navItem.title}</span>
                                                            </>
                                                        )}
                                                    </ResetNavLink>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                currentNavItems.map((navItem) => (
                                    <ResetNavLink
                                        key={navItem.path}
                                        to={navItem.path}
                                        onClick={() => setShowMobileMenu(false)}
                                        className={({ isActive }) => isActive 
                                            ? "nav-item-mobile-active font-bold w-full transition-colors duration-300 ease-out-in" 
                                            : "nav-item-mobile font-medium w-full transition-colors duration-300 ease-out-in group"
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                <Icon icon={navItem.icon} className={`text-xl flex-shrink-0 group-hover:text-primary ${isActive ? 'text-primary' : ''}`} />
                                                <span className="truncate overflow-hidden group-hover:text-font-primary group-hover:font-semibold">{navItem.title}</span>
                                            </>
                                        )}
                                    </ResetNavLink>
                                ))
                            )}
                        </nav>
                    </div>

                    {/* Profile Section */}
                    <div className="px-2 py-3 border-t border-bgAccent">
                        <ProfileButton />
                    </div>
            </div>
        </div>
        </>
    );
};

// Admin Navbar

const AdminNav = ({ expandedTab, setExpandedTab, isCollapsed, onToggleCollapse }) => {
    const { theme } = useTheme();
    const user = useAuthUser();
    const navigate = useNavigate();
    const { currentCustomer, isImpersonating } = useCustomer();

    const [isAdminView, setIsAdminView] = useState(true);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Select the correct Helios logo based on the theme
    const heliosLogo = theme === 'dark' ? heliosLogoDark : heliosLogoLight;

    const toggleView = () => {
        if (isTransitioning) return; // Prevent multiple rapid toggles
        
        setIsTransitioning(true);
        // Start fade out
        setTimeout(() => {
            setIsAdminView(prev => !prev);
            // Start fade in after state change
            setTimeout(() => {
                setIsTransitioning(false);
            }, 50); // Small delay to ensure state change is processed
        }, 150); // Half of transition duration
    }

    // Auto-switch to client view when customer selection happens
    useEffect(() => {
        if (isImpersonating && isAdminView) {
            setIsAdminView(false);
        }
    }, [isImpersonating]); // Only trigger when impersonating state changes, not when isAdminView changes

    // Auto-switch to admin view when impersonation ends
    useEffect(() => {
        if (!isImpersonating && !isAdminView) {
            setIsAdminView(true);
        }
    }, [isImpersonating]); // Only trigger when impersonating state changes, not when isAdminView changes

    const handleSettingsClick = () => {
        navigate('/app/settings');
    }

    // Get the appropriate logo based on current customer context
    const getCurrentLogo = () => {
        // console.log('AdminNav - getCurrentLogo called with isAdminView:', isAdminView, 'currentCustomer:', currentCustomer);
        
        if (!isAdminView && currentCustomer) {
            // console.log('AdminNav - In client view, checking for logo...');
            // First check if customer has a blob storage logo URL
            if (currentCustomer.logoUrl) {
                // console.log('AdminNav - Found logo in logoUrl:', currentCustomer.logoUrl);
                return currentCustomer.logoUrl;
            }

            // Fall back to static clientsData
            const customerData = clientsData.find(client =>
                client.id === currentCustomer.id ||
                client.clientId === currentCustomer.clientId ||
                client.name.toLowerCase() === currentCustomer.name.toLowerCase()
            );
            // console.log('AdminNav - Looking for customer in clientsData:', {
            //     currentCustomerId: currentCustomer.id,
            //     currentCustomerClientId: currentCustomer.clientId,
            //     currentCustomerName: currentCustomer.name,
            //     foundCustomerData: customerData
            // });
            return customerData?.logo || heliosLogo;
        }
        // console.log('AdminNav - Using Helios logo (admin view or no customer)');
        // Default to Helios logo for admin
        return heliosLogo;
    };

    const getCurrentCustomerName = () => {
        if (!isAdminView && currentCustomer) {
            return currentCustomer.name;
        }
        return "Helios Core";
    };

    const { navLinks: adminNavLinks } = useNavigation('admin');
    const { navLinks: clientNavLinks } = useNavigation('client');

    // Categorize admin nav links
    const wardsNavItems = adminNavLinks.filter(nav => nav.path === 'andy-notes' || nav.path === 'equipment-review' || nav.path === 'policy-review');
    const administratorNavItems = adminNavLinks.filter(nav => nav.path !== 'andy-notes' && nav.path !== 'equipment-review' && nav.path !== 'policy-review');

    const [isWardsExpanded, setIsWardsExpanded] = useState(true);
    const [isAdministratorExpanded, setIsAdministratorExpanded] = useState(true);
    const [isWardsTransitioning, setIsWardsTransitioning] = useState(false);
    const [isAdministratorTransitioning, setIsAdministratorTransitioning] = useState(false);

    const toggleAdministrator = () => {
        if (isAdministratorTransitioning) return;
        setIsAdministratorTransitioning(true);
        setTimeout(() => {
            setIsAdministratorExpanded(!isAdministratorExpanded);
            setTimeout(() => {
                setIsAdministratorTransitioning(false);
            }, 50);
        }, 150);
    };

    const toggleWards = () => {
        if (isWardsTransitioning) return;
        setIsWardsTransitioning(true);
        setTimeout(() => {
            setIsWardsExpanded(!isWardsExpanded);
            setTimeout(() => {
                setIsWardsTransitioning(false);
            }, 50);
        }, 150);
    };

    return (
        <div className={`flex flex-col items-stretch gap-4 transition-[width] duration-300 overflow-hidden ${isCollapsed ? 'w-[52px]' : 'w-[200px]'}`}>
            <div className={`flex items-center min-h-[48px] gap-1${isCollapsed ? ' justify-center' : ''}`}>
                {!isCollapsed && (
                    <div className="flex-1 min-w-0 overflow-hidden h-[48px] flex items-center">
                        <img
                            src={getCurrentLogo()}
                            className={`w-full h-full object-contain navbar-logo-transition ${isTransitioning ? 'navbar-logo-fade-out' : 'navbar-logo-fade-in'}`}
                            alt={`${getCurrentCustomerName()} Logo`}
                        />
                    </div>
                )}
                <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="flex-shrink-0 p-1 rounded-md hover:bg-primary-20 text-font-secondary hover:text-primary transition-colors"
                    aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    <Icon icon={isCollapsed ? 'lucide:arrow-right-from-line' : 'lucide:arrow-left-from-line'} className="text-xl" />
                </button>
            </div>
            <div className="flex flex-col gap-4">
                <div
                    className={`flex flex-row ${isCollapsed ? 'justify-center' : 'justify-between'} items-center p-2 rounded-t-md cursor-pointer ${isAdminView ? 'bg-primary-20 border-b-2 border-primary-50' : 'bg-accent-20 border-b-2 border-accent-50'}`}
                    onClick={toggleView}
                >
                    <span className="flex flex-row gap-1 items-center font-semibold">
                        <Icon icon={isAdminView ? "mdi:administrator" : "mdi:user"} className={`text-xl ${isAdminView ? 'text-primary' : 'text-accent'}`} />
                        {!isCollapsed && (isAdminView ? "Admin" : "Client")}
                    </span>
                    {!isCollapsed && <Icon icon={isAdminView ? 'mingcute:toggle-left-fill' : 'mingcute:toggle-right-fill'} className={`responsive-navbar-toggle ${isAdminView ? 'text-primary' : 'text-accent'}`} />}
                </div>
                <nav className={`flex flex-col gap-1 navbar-nav-transition ${isTransitioning ? 'navbar-nav-fade-out' : 'navbar-nav-fade-in'}`}>
                    {isAdminView ? (
                        isCollapsed ? (
                            [...administratorNavItems, ...wardsNavItems].map((nav) => (
                                <ResetNavLink key={nav.path} to={nav.path} title={nav.title}
                                    className={({ isActive }) => isActive
                                        ? "nav-item-active justify-center w-full transition-colors duration-300 ease-out-in"
                                        : "nav-item hover:bg-primary-20 justify-center w-full transition-colors duration-300 ease-out-in"
                                    }
                                >
                                    {({ isActive }) => (
                                        <Icon icon={nav.icon} className={`text-2xl flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                                    )}
                                </ResetNavLink>
                            ))
                        ) : (
                            <>
                                {/* Administrator Dropdown Section */}
                                {administratorNavItems.length > 0 && (
                                    <div className="flex flex-col gap-1 -mt-2">
                                        <button
                                            onClick={toggleAdministrator}
                                            className={`nav-item py-1.5 px-1 hover:rounded-none border-b ${isAdministratorExpanded ? 'border-primary' : 'border-font-secondary hover:border-primary'} hover:bg-transparent font-medium w-full transition-colors duration-300 ease-out-in group flex flex-row items-center justify-between`}
                                        >
                                            <span className={`text-xs font-extrabold uppercase truncate overflow-hidden ${isAdministratorExpanded ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`}>Administrator</span>
                                            <Icon
                                                icon={isAdministratorExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                                                className={`text-xl ${isAdministratorExpanded ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`}
                                            />
                                        </button>
                                        <div className={`flex flex-col gap-1 navbar-nav-transition ${!isAdministratorExpanded && !isAdministratorTransitioning ? 'hidden' : (isAdministratorTransitioning && !isAdministratorExpanded ? 'navbar-nav-fade-out' : 'navbar-nav-fade-in')}`}>
                                            {administratorNavItems.map((nav) => (
                                                <ResetNavLink key={nav.path} to={nav.path}
                                                    className={({ isActive }) => isActive
                                                        ? "nav-item-active font-bold w-full transition-colors duration-300 ease-out-in"
                                                        : "nav-item hover:bg-primary-20 font-medium w-full transition-colors duration-300 ease-out-in group"
                                                    }
                                                >
                                                    {({ isActive }) => (
                                                        <>
                                                            <Icon icon={nav.icon} className={`${isCollapsed ? 'text-2xl' : 'text-lg'} flex-shrink-0 group-hover:text-primary ${isActive ? 'text-primary' : ''}`} />
                                                            <span className="truncate overflow-hidden group-hover:text-font-primary group-hover:font-semibold">{nav.title}</span>
                                                        </>
                                                    )}
                                                </ResetNavLink>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Wards Dropdown Section */}
                                {wardsNavItems.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                        <button
                                            onClick={toggleWards}
                                            className={`nav-item py-1.5 px-1 hover:rounded-none border-b ${isWardsExpanded ? 'border-primary' : 'border-font-secondary hover:border-primary'} hover:bg-transparent font-medium w-full transition-colors duration-300 ease-out-in group flex flex-row items-center justify-between`}
                                        >
                                            <span className={`text-xs font-extrabold uppercase truncate overflow-hidden ${isWardsExpanded ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`}>Wards</span>
                                            <Icon
                                                icon={isWardsExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                                                className={`text-xl ${isWardsExpanded ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`}
                                            />
                                        </button>
                                        <div className={`flex flex-col gap-1 navbar-nav-transition ${!isWardsExpanded && !isWardsTransitioning ? 'hidden' : (isWardsTransitioning && !isWardsExpanded ? 'navbar-nav-fade-out' : 'navbar-nav-fade-in')}`}>
                                            {wardsNavItems.map((nav) => (
                                                <ResetNavLink key={nav.path} to={nav.path}
                                                    className={({ isActive }) => isActive
                                                        ? "nav-item-active font-bold w-full transition-colors duration-300 ease-out-in"
                                                        : "nav-item hover:bg-primary-20 font-medium w-full transition-colors duration-300 ease-out-in group"
                                                    }
                                                >
                                                    {({ isActive }) => (
                                                        <>
                                                            <Icon icon={nav.icon} className={`${isCollapsed ? 'text-2xl' : 'text-lg'} flex-shrink-0 group-hover:text-primary ${isActive ? 'text-primary' : ''}`} />
                                                            <span className="truncate overflow-hidden group-hover:text-font-primary group-hover:font-semibold">{nav.title}</span>
                                                        </>
                                                    )}
                                                </ResetNavLink>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )
                    ) : (
                        clientNavLinks.map((nav) => (
                            <ResetNavLink key={nav.path} to={nav.path} title={isCollapsed ? nav.title : undefined}
                                className={({ isActive }) => isActive
                                    ? `nav-item-active font-bold w-full transition-colors duration-300 ease-out-in${isCollapsed ? ' justify-center' : ''}`
                                    : `nav-item hover:bg-primary-20 font-medium w-full transition-colors duration-300 ease-out-in group${isCollapsed ? ' justify-center' : ''}`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        <Icon icon={nav.icon} className={`${isCollapsed ? 'text-2xl' : 'text-lg'} flex-shrink-0 group-hover:text-primary ${isActive ? 'text-primary' : ''}`} />
                                        {!isCollapsed && <span className="truncate overflow-hidden group-hover:text-font-primary group-hover:font-semibold">{nav.title}</span>}
                                    </>
                                )}
                            </ResetNavLink>
                        ))
                    )}
                </nav>
            </div>
        </div>
    );
}

// Client Navbar

const ClientNav = ({ isCollapsed, onToggleCollapse }) => {

    const { theme } = useTheme();
    const user = useAuthUser();
    const navigate = useNavigate();
    const { currentCustomer } = useCustomer();

    const handleSettingsClick = () => {
        navigate('/app/settings');
    }

    const heliosLogo = theme === 'dark' ? heliosLogoDark : heliosLogoLight;

    // Get client logo based on current customer
    const getClientLogo = () => {
        // console.log('ClientNav - getClientLogo called with currentCustomer:', currentCustomer);
        // console.log('ClientNav - currentCustomer.branding:', currentCustomer?.branding);
        // console.log('ClientNav - currentCustomer.logoUrl:', currentCustomer?.logoUrl);
        // console.log('ClientNav - currentCustomer keys:', currentCustomer ? Object.keys(currentCustomer) : 'null');
        
        if (currentCustomer) {
            // Check multiple possible logo sources for compatibility
            if (currentCustomer.branding?.logo_url) {
                // console.log('ClientNav - Found logo in branding.logo_url:', currentCustomer.branding.logo_url);
                return currentCustomer.branding.logo_url;
            }
            // Fallback to logoUrl property (for admin impersonation)
            if (currentCustomer.logoUrl) {
                // console.log('ClientNav - Found logo in logoUrl:', currentCustomer.logoUrl);
                return currentCustomer.logoUrl;
            }
            // Fallback to branding property without logo_url (if structure is different)
            if (currentCustomer.branding && typeof currentCustomer.branding === 'string') {
                // console.log('ClientNav - Found logo in branding string:', currentCustomer.branding);
                return currentCustomer.branding;
            }

            // Fallback to clientsData for regular client users
            const customerData = clientsData.find(client =>
                client.id === currentCustomer.id ||
                client.clientId === currentCustomer.clientId ||
                client.name.toLowerCase() === currentCustomer.name.toLowerCase()
            );
            // console.log('ClientNav - Looking for customer in clientsData:', {
            //     currentCustomerId: currentCustomer.id,
            //     currentCustomerClientId: currentCustomer.clientId,
            //     currentCustomerName: currentCustomer.name,
            //     foundCustomerData: customerData
            // });
            if (customerData?.logo) {
                // console.log('ClientNav - Found logo in clientsData:', customerData.logo);
                return customerData.logo;
            }
        }
        // console.log('ClientNav - No logo found, falling back to Helios logo');
        // Fall back to Helios logo if no client logo is found
        return heliosLogo;
    };

    const { navLinks: clientNavLinks } = useNavigation('client');

    return (
        <div className={`flex flex-col items-stretch gap-4 transition-[width] duration-300 overflow-hidden ${isCollapsed ? 'w-[52px]' : 'w-[200px]'}`}>
            <div className={`flex items-center min-h-[48px] gap-1${isCollapsed ? ' justify-center' : ''}`}>
                {!isCollapsed && getClientLogo() && (
                    <div className="flex-1 min-w-0 overflow-hidden h-[48px] flex items-center">
                        <img src={getClientLogo()} className="w-full h-full object-contain" alt={`${currentCustomer?.name || "Client"} Logo`} />
                    </div>
                )}
                <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="flex-shrink-0 p-1 rounded-md hover:bg-primary-20 text-font-secondary hover:text-primary transition-colors"
                    aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    <Icon icon={isCollapsed ? 'lucide:arrow-right-from-line' : 'lucide:arrow-left-from-line'} className="text-xl" />
                </button>
            </div>
            <div className="flex flex-col gap-2">
                <nav className="flex flex-col gap-1">
                    {clientNavLinks.map((clientNav) => (
                        <ResetNavLink key={clientNav.path} to={clientNav.path} title={isCollapsed ? clientNav.title : undefined}
                            className={({ isActive }) => isActive
                                ? `nav-item-active font-bold w-full transition-colors duration-300 ease-out-in${isCollapsed ? ' justify-center' : ''}`
                                : `nav-item font-medium w-full transition-colors duration-300 ease-out-in group${isCollapsed ? ' justify-center' : ''}`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <Icon icon={clientNav.icon} className={`${isCollapsed ? 'text-2xl' : 'text-lg'} flex-shrink-0 group-hover:text-primary ${isActive ? 'text-primary' : ''}`} />
                                    {!isCollapsed && <span className="truncate overflow-hidden group-hover:text-font-primary group-hover:font-semibold">{clientNav.title}</span>}
                                </>
                            )}
                        </ResetNavLink>
                    ))}
                </nav>
            </div>
            {/* <div className="flex flex-col gap-2">
                <span className="flex flex-row gap-1 items-center text-primary">
                    <Icon icon="mdi:administrator-outline" className="text-xl" />
                    Administrator
                </span>
                <button className="flex flex-row items-center justify-center gap-1 text-font-secondary bg-background shadow-neo-page rounded-md p-2">
                    <Icon icon="fluent:person-call-24-regular" className="text-lg text-accent" />
                    Contact Administrator
                </button>
            </div> */}
        </div>
    )
}

// Dynamic Navbar based on Role

export const Navbar = ({ expandedClients, setExpandedClients, searchQuery, setSearchQuery, handleSearch, searchInputRef, isRefreshing, handleGlobalRefresh, showMobileMenu, setShowMobileMenu }) => {

    const user = useAuthUser();

    const isAdmin = user.role === 'helios_admin';

    const navigate = useNavigate();
    const [isNavCollapsed, setIsNavCollapsed] = useState(false);

    const handleSettingsClick = () => {
        navigate('/app/settings');
    };

    // Debug: Log what we're getting
    // console.log('Navbar - User object:', user);
    // console.log('Navbar - User role:', user.role);
    // console.log('Navbar - Is admin?:', isAdmin);
    // console.log('Navbar - User roles array:', user.roles);
    // console.log('Navbar - Is Helios admin flag:', user.isHeliosAdmin);

    return (
        <>
            {/* Mobile Navbar (< 1024px) */}
            <div className="lg:hidden w-full">
                <ResponsiveMobileNav 
                    user={user} 
                    isAdmin={isAdmin}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    handleSearch={handleSearch}
                    searchInputRef={searchInputRef}
                    isRefreshing={isRefreshing}
                    handleGlobalRefresh={handleGlobalRefresh}
                    showMobileMenu={showMobileMenu}
                    setShowMobileMenu={setShowMobileMenu}
                />
            </div>

            {/* Desktop Navbar (>= 1024px) */}
            <aside className="hidden h-full lg:flex navbar flex-col justify-between text-font-primary">
                {isAdmin ? (
                    <AdminNav
                        expandedTab={expandedClients}
                        setExpandedTab={setExpandedClients}
                        isCollapsed={isNavCollapsed}
                        onToggleCollapse={() => setIsNavCollapsed(prev => !prev)}
                    />
                ) : (
                    <ClientNav
                        isCollapsed={isNavCollapsed}
                        onToggleCollapse={() => setIsNavCollapsed(prev => !prev)}
                    />
                )}

                <ProfileButton isCollapsed={isNavCollapsed} />
            </aside>
        </>
    );
};